import { ShapeType, BShape } from './BShape';
import { CopyUtils, NumPair, Point } from '../../Misc/Utils';
import { ObjectType } from '../ObjectTypeEnum';
import { ColouringMode } from '../../Colour/Colourable';
import { BVectorEditor } from '../ObjectConstructors/BVectorEditor';
import { PointOps } from '../../Graphics/PointOps';
import { VectorStruct } from './Structure/VectorStruct';
import { InputListener, InputRegisterer } from '../../Input/InputHandler';
import { SelectionCircle } from '../../CanvasGui/SelectionBox';

export enum BVectorMode{
    None,
    Build,
    Edit,
    ControlPointDragging
}

export class BVector extends BShape implements InputListener{
    protected readonly TYPE = ObjectType.VECTOR;

    protected structure : VectorStruct = new VectorStruct();
    protected windingDirection = 0; //1-clockwise 0-counter clock wise

    protected editor: BVectorEditor = new BVectorEditor(this.structure, this.structure.getVertices(), this.structure.getControlPoints()); //sending by reference
    protected mode: BVectorMode = BVectorMode.None;
    
    private SNAPPING_SENSITIVITY = 10;
    private SCALE_SENSITIVITY = 10;
    
    constructor(firstPoint: Point, frameRange : NumPair){
        //first point is considered the center for the vector. rest of the points should be added relative to the center.
        super(firstPoint, ShapeType.VECTOR, frameRange);
        this.addPoint([0,0])
        this.setWH([0,0]);
        this.setColouringMode(ColouringMode.Stroke);
    }

    addAsRelativePoint(pt: Point){
        this.addPoint(this.getPointRelativeToCentre(pt));
        this.recomputeCenterAndWH();
    }

    private recomputeCenterAndWH(){
        var change = PointOps.mid(this.structure.getMin(), this.structure.getMax());
        this.setCentre(PointOps.add(this.centre, change)); //minxy and maxxy are relative to center
        this.moveVerticesAndControlPointsBy(PointOps.multiply(change, -1)); //since center is moved by 'change', other points are relative and so move by '-change'
        this.setWH(PointOps.difference(this.structure.getMin(), this.structure.getMax()));//min and max xy are updated by struct after moveVerticesAndControlPointsBy() call
        this.windingDirection = this.structure.getWinding();
    }

    private moveVerticesAndControlPointsBy(change: Point){
        this.structure.translate(change);
    }

    /**
     * User is moving the mouse and as a result the last vertex needs to track the mouse
     * @param pt 
     */
    setLastVertexAsRelative(pt: Point){
        var curPoint = this.getPointRelativeToCentre(pt)
        if(PointOps.containedInCircle(curPoint, this.structure.getVertex(0), this.SNAPPING_SENSITIVITY)){ //snapping sensitivity is 10
            this.setLastVertex(this.structure.getVertex(0));
        }else{
            this.setLastVertex(curPoint);
        }
    }

    setControlPointOfLastButOneVertexAsRelative(pt: Point){
        var endCtrlPt = this.getPointRelativeToCentre(pt);
        if(this.structure.getVertexCount()-2>0){
            var lastButOne = this.structure.getVertexCount() - 2;
            var diff = PointOps.subtract(this.structure.getVertex(lastButOne), endCtrlPt);
            var startCtrlPt = PointOps.add(this.structure.getVertex(lastButOne), diff);
            this.structure.setControlPoint(lastButOne, startCtrlPt, endCtrlPt);
        }
    }

    private setLastVertex(pt: Point){
        var len = this.structure.getVertexCount();
        this.structure.setVertex(len-1, pt);
    }

    removeLastVertex(){
        this.structure.removeVertex();
    }

    private addPoint(pt: Point){
        this.structure.addVertex(pt)
    }

    setBVectorMode(newMode: BVectorMode){
        if(this.mode == BVectorMode.Edit){
            this.editor.setupEditing();
        }
        this.mode = newMode;
    }

    evaluateShouldCloseVectorPath(){
        //if the last added point is same as the first point of vector then user intends to close the vector. 
        //if shouldClose is set then the line cap (miter/round/bevel) are displayed perfectly as a single shape.
        var count = this.structure.getVertexCount()
        if(count > 2){
            var isContained = PointOps.containedInCircle(this.structure.getVertex(count-1), this.structure.getVertex(0), this.SNAPPING_SENSITIVITY);
            this.structure.setLoop( isContained );
            if(isContained){
                this.structure.setVertex(count-1, this.structure.getVertex(0));
                //this.structure.removeVertex(); //we call ctx.closePath to close the loop. so the last vertex the user clicked is not necessary.
            }
        }else{
            this.structure.setLoop(false);
        }
    }

    isClosedLoop(): boolean{
        if(this.mode == BVectorMode.Build){
            return this.structure.getVertexCount() > 2 && PointOps.containedInCircle(this.structure.getLastVertex(), this.structure.getFirstVertex(), this.SNAPPING_SENSITIVITY);
        }else{
            return this.structure.isLoop();
        }
    }

    /*
    protected scale(curMousePoint: Point): void{
        var oldWH = [this.wh[0],this.wh[1]];
        super.scale(curMousePoint);
        var change = PointOps.subtract(this.wh, oldWH as Point);
        var percent = PointOps.multiply(PointOps.divByPair(change, oldWH as NumPair), 2);
        this.scaleFactor = PointOps.add(this.scaleFactor, percent);
    }
    */

    //Gives the point on the vector shape of the selected tx point 
    private getTxPoint(selectedTxPointIndex: SelectionCircle): Point{
        var anchor: Point = [0,0]
        switch(selectedTxPointIndex){
            case SelectionCircle.TOP_LEFT:
                anchor = this.structure.getMin(); 
            break;
            case SelectionCircle.TOP_RIGHT:
                anchor = [this.structure.getMax()[0], this.structure.getMin()[1]];
            break;
            case SelectionCircle.BOTTOM_RIGHT:
                anchor = this.structure.getMax();
            break;
            case SelectionCircle.BOTTOM_LEFT:
                anchor = [this.structure.getMin()[0], this.structure.getMax()[1]];
            break;
            case SelectionCircle.TOP:
                anchor = [0, this.structure.getMin()[1]];
            break;
            case SelectionCircle.RIGHT:
                anchor = [this.structure.getMax()[0], 0]; 
            break;
            case SelectionCircle.BOTTOM:
                anchor = [0, this.structure.getMax()[1]];
            break;
            case SelectionCircle.LEFT:
                anchor = [this.structure.getMin()[0], 0];
            break;
        }
        return anchor;
    }

    //Supplies the opposite extreme (point in the vector) of the given selection tx point.
    private getAnchor(selectedTxPointIndex: SelectionCircle): Point{
        var anchor: Point = [0,0]
        switch(selectedTxPointIndex){
            case SelectionCircle.TOP_LEFT:
                anchor = this.structure.getMax(); //basically bottom right is the extreme opposite
            break;
            case SelectionCircle.TOP_RIGHT:
                anchor = [this.structure.getMin()[0], this.structure.getMax()[1]];
            break;
            case SelectionCircle.BOTTOM_RIGHT:
                anchor = this.structure.getMin();
            break;
            case SelectionCircle.BOTTOM_LEFT:
                anchor = [this.structure.getMax()[0], this.structure.getMin()[1]];
            break;
            case SelectionCircle.TOP:
                anchor = [0, this.structure.getMax()[1]];
            break;
            case SelectionCircle.RIGHT:
                anchor = [this.structure.getMin()[0], 0]; //opposite extreme is the left most point which is minimum of X
            break;
            case SelectionCircle.BOTTOM:
                anchor = [0, this.structure.getMin()[1]];
            break;
            case SelectionCircle.LEFT:
                anchor = [this.structure.getMax()[0], 0];
            break;
        }
        return anchor;
    }
   
    protected scalingImplm(selectedTxPointIndex: SelectionCircle, localCurMousePoint: Point): void{
        var anchor = this.getAnchor(selectedTxPointIndex)
        var selectedTxPoint = this.getTxPoint(selectedTxPointIndex)
        
        var scale = PointOps.divByPair(PointOps.subtract(localCurMousePoint, selectedTxPoint), this.structure.getMax()); 

        //Make sure unnecessary dim i.e. x or y side is not scaled
        switch(selectedTxPointIndex){
            case SelectionCircle.TOP:
            case SelectionCircle.BOTTOM:
                scale[0] = 0
            break;
            case SelectionCircle.RIGHT:
            case SelectionCircle.LEFT:
                scale[1] = 0
            break;
        }

        //console.log("scale:"+scale);
        this.scaleBy(scale[0], scale[1], anchor[0], anchor[1]);            
    }

    //this still doesnt work with [0,0] as the anchor. only works when anchor is one of the tx selection points
    //doesnt work properly with rotation or inversion while scaling.
    private scaleBy(scaleX: number, scaleY: number, anchorX: number, anchorY: number){
        var maxXDist = 0.0;
        var maxYDist = 0.0;

        maxXDist = this.structure.getMax()[0];
        maxYDist = this.structure.getMax()[1];
        /*if(Math.abs(this.structure.getMax()[0] - anchorX) >  Math.abs(this.structure.getMin()[0] - anchorX)){
            maxXDist = Math.abs(this.structure.getMax()[0] - anchorX)
        }else{
            maxXDist = Math.abs(this.structure.getMin()[0] - anchorX)
        }

        if(Math.abs(this.structure.getMax()[1] - anchorY) >  Math.abs(this.structure.getMin()[1] - anchorY)){
            maxYDist = Math.abs(this.structure.getMax()[1] - anchorY)
        }else{
            maxYDist = Math.abs(this.structure.getMin()[1] - anchorY)
        }*/

        var count = this.structure.getVertexCount();
        for(var i=0; i<count; i++){
            var vert = this.structure.getVertex(i);
            var scaleXPercent = ( Math.abs(vert[0]-anchorX)/maxXDist ) * scaleX;
            var scaleYPercent = ( Math.abs(vert[1]-anchorY)/maxYDist ) * scaleY;
            this.structure.translateEntity( i, [this.SCALE_SENSITIVITY*scaleXPercent, this.SCALE_SENSITIVITY*scaleYPercent] );
        }

        this.recomputeCenterAndWH();    
    }

    //override
    /**
     * Method to check if any vertex or bezier line control point is selected
     * @param curPoint is the global to local converted point
     */
    protected checkControlPointSelection(curPoint: Point): boolean{
        if(this.mode == BVectorMode.Edit){
            return this.editor.isControlPointSelected(curPoint);
        }else{
            return super.checkControlPointSelection(curPoint);
        }
    }

    //overrides
    isEditableStructure(): boolean{ return true; }

    //overrides
    editStructure(start: boolean, inputRegisterer: InputRegisterer):void{
        if(start){
            this.setBVectorMode(BVectorMode.Edit);  
            inputRegisterer.addListener(this);
        }else{
            inputRegisterer.removeListener(this);
            this.setBVectorMode(BVectorMode.None);
        }
    }

    onMouseDown(point: Point): void {
        //do nothing
    }
    onMouseUp(point: Point): void {
        if(this.mode == BVectorMode.Edit){
            this.editor.onSingleClick(this.globalToLocal(point))
        }
    }
    onMouseMove(from: Point, to: Point): void {
        //do nothing
    }
    onDoubleClick(point: Point): void {
        if(this.mode == BVectorMode.Edit){
            this.editor.onDoubleClick(this.globalToLocal(point));
        }
    }

    protected editImplm(curMousePoint: Point, change: Point):void {
        switch(this.mode){
            case BVectorMode.Edit:
                var vertexPosChanged = this.editor.editVertexAndControlPoints(this.globalToLocal(curMousePoint));
                if(vertexPosChanged){
                    //recalculate bounding box
                    this.recomputeCenterAndWH();
                }
                break;
            default:
                super.editImplm(curMousePoint, change);
        }
    }

    protected drawEditorPoints(canvasCtx : CanvasRenderingContext2D){
        switch(this.mode){
            case BVectorMode.Build:
                this.editor.draw(canvasCtx);
                break;
            case BVectorMode.Edit:
                this.editor.drawControlPoints(canvasCtx);
                this.editor.draw(canvasCtx);
                break;
        }
    }

    //override
    drawControlPoints(canvasCtx : CanvasRenderingContext2D, curFrame : number) : void{
        //When shape is selected to edit by double clicking, then bounding box need not be shown
        if(this.mode!=BVectorMode.Edit){
            super.drawControlPoints(canvasCtx, curFrame);
        }
    }

    protected drawShape(canvasCtx : CanvasRenderingContext2D): void{
        canvasCtx.beginPath();
        var firstVertex = this.structure.getVertex(0);
        canvasCtx.moveTo(firstVertex[0], firstVertex[1]);

        var verticesLength = this.structure.getVertexCount();
        if(this.mode == BVectorMode.ControlPointDragging){
            verticesLength--; //since while building,last vertex is considerd as a mouse tracking vertex
        }

        for(var i = 1; i < verticesLength; i++){
            var v = this.structure.getVertex(i);
            var cpi1 = this.structure.getControlPoint(i-1);
            var cpi = this.structure.getControlPoint(i);
            if(this.windingDirection){ // clock wise winding directiion
                canvasCtx.bezierCurveTo( 
                    cpi1[VectorStruct.END_CTRL_PT][0], cpi1[VectorStruct.END_CTRL_PT][1],
                    cpi[VectorStruct.START_CTRL_PT][0], cpi[VectorStruct.START_CTRL_PT][1],
                    v[0], v[1])
            }else{ 
                //counter clockwise direction for bezier control points
                canvasCtx.bezierCurveTo( 
                    cpi1[VectorStruct.START_CTRL_PT][0], cpi1[VectorStruct.START_CTRL_PT][1],
                    cpi[VectorStruct.END_CTRL_PT][0], cpi[VectorStruct.END_CTRL_PT][1],
                    v[0], v[1])
            }
        }
        if(this.structure.isLoop()){
            canvasCtx.closePath();
        }
        this.drawPath(canvasCtx);
        this.drawEditorPoints(canvasCtx);
    }

    private drawPath(canvasCtx : CanvasRenderingContext2D){
        switch(this.colorMgr.getColouringMode()){
            case ColouringMode.None:
                //do nothing
                break;
            case ColouringMode.Fill:
                canvasCtx.fill();
                break;
            case ColouringMode.Stroke:
                canvasCtx.stroke();
                break;
            case ColouringMode.FillAndStroke:
                canvasCtx.fill();
                canvasCtx.stroke();
                break;
        }
    }

    public clone() : BVector{
        var clone = new BVector(this.centre, this.getFrameRange());
        clone.structure = this.structure.clone();
        clone.windingDirection = this.windingDirection;
        clone.editor = new BVectorEditor(clone.structure, clone.structure.getVertices(), clone.structure.getControlPoints());
        super.duplicate(clone);
        return clone;
    }
}